import { useState, useEffect, useMemo } from 'react';
import { Accordion, Card, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { isEmpty } from 'lodash';
import './LicensorMaster.css';
import CustomTooltip from '../../../Components/Tooltip/CustomTooltip';
import Loader from '../../../Components/Utility/Loader/Loader';
import ConfirmationPopup from '../../../Components/ModalPopup/ConfirmationPopup';
import * as action from '../Store/SystemMasters.action';
import { useComponent } from '../../UserSetting/UsersLibrary/UserRights';

const TEMP_LICENSOR = {
  name: null,
  aliasName: '',
  isEmpty: false,
  isNew: true,
  isActive: true,
  _id: '',
};

function CustomAttrSave(isLoading) {
  return isLoading ? <Spinner animation="border" /> : <i className="icon-Tick-1 f-24" />;
}

function LicensorMaster() {
  const dispatch = useDispatch();
  const { t } = useTranslation('common');

  const {
    SystemMastersDetails: { licensorList },
    UserDetails: { userInfo },
    Users: { logInRights },
  } = useSelector((state) => state);

  const { CheckActionPermission } = useComponent();

  const MasterAddEditAccess = CheckActionPermission(logInRights, 'masters', 'licensor', 'addEdit');
  const MasterDeleteAccess = CheckActionPermission(logInRights, 'masters', 'licensor', 'delete');

  const loggedInUserId = userInfo.userId;

  const [licensorData, setLicensorData] = useState([]);
  const [showLicensor, setShowLicensor] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [addingLicensors, setAddingLicensors] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteId, setDeleteId] = useState('');
  const [toggleDelete, setToggleDelete] = useState(false);
  const [toggleActiveInactive, setToggleActiveInactive] = useState(false);
  const [activeInactiveID, setActiveInactiveID] = useState('');
  const [licensorAccess, setLicensorAccess] = useState();
  const btnDisabled = useMemo(() => licensorData?.some((item) => !item.name?.trim()), [licensorData]);

  useEffect(() => {
    const list = licensorList;
    if (addingLicensors && list) {
      setLicensorData([...list, { ...TEMP_LICENSOR }]);
    } else {
      setLicensorData(list);
    }
  }, [licensorList, addingLicensors]);

  const fetchLicensors = async () => {
    if (!showLicensor) {
      setIsLoading(true);
      setShowLicensor(true);
      await dispatch(action.getLicensorDetails({ loggedInUserId }));
      setIsLoading(false);
    } else {
      setShowLicensor(false);
    }
  };

  const handleInputChange = (event, index) => {
    const { name, value } = event.target;
    const updated = [...licensorData];

    updated[index] = {
      ...updated[index],
      [name]: value,
      isEmpty: name === 'name' && value.trim() === '',
    };
    setLicensorData(updated);
  };

  const duplicateIndices = useMemo(() => {
    const seen = {};
    const duplicates = [];

    licensorData?.forEach((item, i) => {
      const name = item.name?.trim().toLowerCase();
      if (name) seen[name] !== undefined ? duplicates.push(i, seen[name]) : (seen[name] = i);
    });

    return [...new Set(duplicates)];
  }, [licensorData]);

  const addLicensor = () => {
    if (!showLicensor) {
      fetchLicensors();
      setAddingLicensors(true);
    } else {
      setLicensorData((prev) => [...prev, { ...TEMP_LICENSOR }]);
    }
  };

  const handleDelete = (id) => {
    if (typeof id === 'number') {
      setLicensorData((prev) => prev.filter((_, i) => i !== id));
    } else {
      setDeleteId(id);
      setToggleDelete(true);
    }
  };

  const handleSave = async () => {
    const hasEmptyName = licensorData?.some((item) => isEmpty(item.name?.trim()));
    if (hasEmptyName || duplicateIndices.length > 0) {
      const updated = licensorData?.map((item) => ({
        ...item,
        isEmpty: isEmpty(item.name?.trim()),
      }));
      setLicensorData(updated);
      return;
    }

    setIsSaving(true);
    const payload = licensorData.map((item) => ({
      ...item,
      name: item.name.trim(),
      aliasName: item.aliasName.trim(),
    }));

    await dispatch(action.saveToSystemMaster({ names: JSON.stringify(payload), title: 'licensor' }, t));
    await dispatch(action.getLicensorDetails({ loggedInUserId }));

    setAddingLicensors(false);
    setIsSaving(false);
  };

  const confirmDelete = async () => {
    setIsLoading(true);
    await dispatch(action.deleteFromSystemMasters({ loggedInUserId, licensorId: deleteId, title: 'licensor' }, t));
    await dispatch(action.getLicensorDetails({ loggedInUserId }));
    setIsLoading(false);
    setToggleDelete(false);
  };

  const confirmToggleActive = async () => {
    const licensor = licensorData.find((l) => l._id === activeInactiveID);
    if (licensor) {
      await dispatch(action.setActiveInactive({ id: licensor._id, isActive: !licensor.isActive, loggedInUserId, title: 'licensor' }, t));
      await dispatch(action.getLicensorDetails({ loggedInUserId }));
      setToggleActiveInactive(false);
    }
  };

  const handleSwitch = async (e) => {
    setLicensorAccess(!licensorAccess);
    await dispatch(action.setLicensorAccess({ isActiveLicensor: e.target.checked }));
  };

  useEffect(() => {
    const Init = async () => {
      const licensorAccessRes = await dispatch(action.getLicensorAccess());
      setLicensorAccess(licensorAccessRes?.isActiveLicensor);
    };
    Init();
  }, []);

  return (
    <>
      <Accordion className="mb-4 master-accordian custom-attribute-master">
        <Card>
          <Card.Header>
            <div className="title-container d-flex">
              <div className="custom-control custom-switch">
                {MasterAddEditAccess && (
                  <input type="checkbox" className="custom-control-input" id="customSwitch2" checked={licensorAccess} onChange={(e) => handleSwitch(e)} />
                )}
                <label className="custom-control-label" htmlFor="customSwitch2" />
              </div>
              <p>{t('LICENSOR_MASTER.TITLE')}</p>
            </div>
            <div className="action-title-btns">
              <CustomTooltip tooltipText={t(showLicensor ? 'CUSTOM_ATTRIBUTES.CLOSE' : 'CUSTOM_ATTRIBUTES.OPEN')}>
                <button
                  className="btn d-inline-flex p-0"
                  onClick={fetchLicensors}
                  aria-label={t(showLicensor ? 'CUSTOM_ATTRIBUTES.CLOSE' : 'CUSTOM_ATTRIBUTES.OPEN')}
                >
                  <i className={`icon-new-down-arrow-icon ${showLicensor ? 'icon-inverse' : ''} f-24`} />
                </button>
              </CustomTooltip>
            </div>
          </Card.Header>
          {showLicensor && (
            <Card.Body>
              {isLoading ? (
                <Loader />
              ) : (
                <>
                  <div className="common-scrollbar flex-fill">
                    <ul>
                      {licensorData.map((item, index) => (
                        <li key={index}>
                          <input
                            name="aliasName"
                            value={item.aliasName}
                            maxLength={5}
                            onChange={(e) => handleInputChange(e, index)}
                            disabled={!MasterAddEditAccess}
                            className="w-50px mr-2"
                          />
                          <div className="input-container">
                            <input
                              placeholder={t('LICENSOR_MASTER.LICENSOR_PLACEHOLDER')}
                              name="name"
                              maxLength={30}
                              value={item.name || ''}
                              autoFocus={!item._id}
                              onChange={(e) => handleInputChange(e, index)}
                              disabled={!MasterAddEditAccess}
                            />
                            {duplicateIndices.includes(index) && (
                              <label className="text-danger m-0 p-1">{t('SYSTEM_MASTER_TABLE.THIS_ENTRY_ALREADY_EXISTS')}</label>
                            )}
                            {item.isEmpty && (
                              <label className="text-danger m-0 p-1">
                                {t('LICENSOR_MASTER.TITLE')} {t('SYSTEM_MASTER_TABLE.ENTRY_REQUIRED')}
                              </label>
                            )}
                          </div>
                          <div className="input-action d-flex">
                            {item._id && MasterAddEditAccess && (
                              <CustomTooltip tooltipText={t('SYSTEM_MASTER_TABLE.ACTIVE/INACTIVE')}>
                                <button
                                  type="button"
                                  className="btn p-0 custom-action mr-0"
                                  onClick={() => {
                                    setActiveInactiveID(item._id);
                                    setToggleActiveInactive(true);
                                  }}
                                  id={item?._id}
                                  aria-label={item?.isActive ? 'Active' : 'Inactive'}
                                >
                                  <span className={item.isActive ? 'icon-Eye-Open' : 'icon-Eye-Closed'} />
                                </button>
                              </CustomTooltip>
                            )}
                            {MasterDeleteAccess && (
                              <CustomTooltip tooltipText={t('SYSTEM_MASTER_TABLE.DELETE')}>
                                <button type="button" className=" btn custom-action mr-0" onClick={() => handleDelete(item._id || index)} aria-label="delete">
                                  <span className="icon-Delete f-24" />
                                </button>
                              </CustomTooltip>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="licensor-btn-container">
                    {MasterAddEditAccess && (
                      <CustomTooltip tooltipText={t('CUSTOM_ATTRIBUTES.ADD_TOOLTIP')}>
                        <button className="btn btn-outline sm ml-auto mr-0 mt-16 p-0" onClick={addLicensor} disabled={btnDisabled || duplicateIndices.length}>
                          <span className="icon-Add text-grey" />
                        </button>
                      </CustomTooltip>
                    )}
                    {MasterAddEditAccess && (
                      <div className="section-custom-save">
                        <button
                          className="btn p-0"
                          onClick={handleSave}
                          disabled={!licensorData.length || btnDisabled || duplicateIndices.length || isSaving}
                          aria-label="Save"
                        >
                          {CustomAttrSave(isSaving)}
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </Card.Body>
          )}
        </Card>
      </Accordion>
      {toggleDelete && (
        <ConfirmationPopup
          onOpen={toggleDelete}
          onConfirm={confirmDelete}
          title={t('LICENSOR_MASTER.DELETE_CONFIRMATION.TITLE')}
          message={t('LICENSOR_MASTER.DELETE_CONFIRMATION.MESSAGE')}
          toggle={() => setToggleDelete(false)}
          loading={isLoading}
        />
      )}
      {toggleActiveInactive && (
        <ConfirmationPopup
          onOpen={toggleActiveInactive}
          onConfirm={confirmToggleActive}
          title={t('LICENSOR_MASTER.ACTIVE_INACTIVE_CONFIRMATION.TITLE')}
          message={t('LICENSOR_MASTER.ACTIVE_INACTIVE_CONFIRMATION.MESSAGE')}
          toggle={() => setToggleActiveInactive(false)}
          loading={isLoading}
        />
      )}
    </>
  );
}

export default LicensorMaster;
